/**
 * The OIDC redirects that land on a real path, resolved before the HashRouter
 * app mounts (the router can't see them). Mirrors the brewlens
 * `resolveInitialAuth` pattern. Returns 'popup' when this window is the
 * re-auth popup and 'frame' when it is the re-auth modal's embedded sign-in
 * frame; neither may ever mount the app.
 */
import { userManager } from './reauthClient';
import { clearLocalSignoutIfFreshSignIn, clearLocalSignoutPending } from './localSignoutMarker';

export async function resolveAuthRedirects(): Promise<'popup' | 'frame' | 'app'> {
  const path = window.location.pathname;

  if (path === '/auth/callback') {
    // Both the login redirect AND the re-auth popup come back to
    // /auth/callback. A popup window has an `opener`; the top-level login
    // redirect does not. Reusing one callback path avoids requiring the SPA
    // host to serve a second route — a dedicated /auth/popup-callback 404s
    // wherever the host (e.g. director serving the built SPA) doesn't map it
    // to index.html, while /auth/callback is already served everywhere login
    // works.
    const inPopup = !!window.opener && window.opener !== window;
    // The re-auth modal's embedded sign-in (@brew/ui embeddedReauth) lands
    // here inside its iframe: a parent and no opener. An opener always means
    // the popup, even one on this same origin. signinCallback() on the
    // frame's 'si:s' state posts the response to the parent, from this
    // origin.
    const inFrame = !inPopup && window.parent !== window;
    try {
      if (inPopup) {
        await userManager.signinPopupCallback();
      } else if (inFrame) {
        await userManager.signinCallback();
      } else {
        // A credentialed sign-in after a failed sign-out ends it (#2272).
        clearLocalSignoutIfFreshSignIn(await userManager.signinCallback());
      }
    } catch (e) {
      console.error('oidc callback', e);
    }
    if (inPopup) {
      // The popup has notified its opener (which resolves signinPopup and
      // closes it); never mount the app inside the popup window.
      return 'popup';
    }
    if (inFrame) {
      // The parent closes the frame once it has the response; a failed
      // callback leaves it to the modal's watchdog. Either way the frame
      // renders nothing.
      return 'frame';
    }
    // Top-level redirect: return the user to where they were before we
    // redirected them to brewauth (stashed by ProtectedRoute / the reauth
    // modal), instead of always landing on home. The value is a HashRouter
    // hash like "#/build/123"; fall back to root when absent.
    const returnHash = sessionStorage.getItem('postLoginHash') ?? '';
    sessionStorage.removeItem('postLoginHash');
    window.history.replaceState({}, '', `/${returnHash}`);
  } else if (path === '/' && new URLSearchParams(window.location.search).has('state')) {
    // brewauth's post-logout landing. A `state` that matches the sign-out
    // request this app stored proves end_session completed, which ends a
    // pending local sign-out (#2272). Anything else (no match, an error)
    // leaves the marker set: failing closed costs one credential prompt.
    try {
      if (await userManager.signoutCallback()) clearLocalSignoutPending();
    } catch (e) {
      console.error('oidc signout callback', e);
    }
    window.history.replaceState({}, '', `/${window.location.hash}`);
  }
  return 'app';
}
